'use client'

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import CountUp from 'react-countup';

interface FlagshipFeature {
  icon: string;
  title: string;
  description: string;
  metrics: string;
}

interface KenyaFlagshipModelProps {
  className?: string;
}

const KenyaFlagshipModel = ({ className = '' }: KenyaFlagshipModelProps) => {
  const flagshipFeatures: FlagshipFeature[] = [
    {
      icon: 'BuildingOffice2Icon',
      title: 'Government Partnership Framework',
      description: 'Structured collaboration with national and county governments, ensuring regulatory alignment and policy support for sustainable development initiatives.',
      metrics: '8 Government MOUs Signed'
    },
    {
      icon: 'UserGroupIcon',
      title: 'Local Workforce Development',
      description: 'Comprehensive training programs creating skilled labor pools, with emphasis on youth employment and technical capacity building across priority sectors.',
      metrics: '15,000+ Jobs Created'
    },
    {
      icon: 'CurrencyDollarIcon',
      title: 'Infrastructure Investment',
      description: 'Strategic capital deployment in transportation, energy, and digital infrastructure, enabling economic activity and reducing operational barriers.',
      metrics: 'USD 180M Deployed'
    },
    {
      icon: 'ChartBarIcon',
      title: 'Sector-Specific Interventions',
      description: 'Targeted investments in agriculture, manufacturing, and technology sectors with measurable impact on productivity and value chain development.',
      metrics: '24 Active Projects'
    }
  ];

  return (
    <section className={`py-8 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mb-4 sm:mb-16">
          <h2 className="font-headline text-3xl md:text-4xl font-semibold text-foreground mb-2 sm:mb-6">
            <span className="text-accent">Kenya's Flagship Model</span>: Systematic Approach to Regional Development
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary leading-relaxed">
            Our Kenya operations serve as the proof-of-concept for scalable, sustainable development finance. Through disciplined execution and authentic partnership, we've established replicable frameworks that balance economic returns with measurable social impact.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="hidden sm:block relative h-96 lg:h-full rounded-xl overflow-hidden shadow-institutional">
            <AppImage
              src="https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg"
              alt="Modern Kenyan business district with glass office buildings and busy street showing economic development"
              fill
              className="object-cover"
            />
          </div>

          <div className="space-y-6">
            {flagshipFeatures.map((feature, index) => (
              <div key={index} className="bg-card rounded-lg p-3 sm:p-6 border border-border hover:shadow-md transition-shadow duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={feature.icon as any} size={24} variant="outline" className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-headline text-xl font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="font-body text-sm text-text-secondary leading-relaxed mb-3">
                      {feature.description}
                    </p>
                    <div className="inline-flex items-center space-x-2 bg-accent/10 px-3 py-1 rounded-full">
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-accent" />
                      <span className="font-body text-xs font-medium text-accent">{feature.metrics}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-900 to-black bg-[length:200%_200%] animate-gradient-x rounded-xl p-4 md:p-12 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-4xl font-headline font-semibold text-accent mb-2"><CountUp end={2} duration={10} decimals={0} />K</div>
              <div className="font-body font-bold text-sm text-gray-200">Operations Established</div>
            </div>
            <div>
              <div className="text-4xl  font-headline font-semibold text-accent mb-2"><CountUp end={47} duration={10} decimals={0} /></div>
              <div className="font-body font-bold text-sm text-gray-200">Counties Accessible</div>
            </div>
            <div>
              <div className="text-4xl font-headline font-semibold text-accent mb-2"><CountUp end={98} duration={10} decimals={0} />%</div>
              <div className="font-body font-bold text-sm text-gray-200">Regulatory Compliance Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KenyaFlagshipModel;